export const MIN_EXAM_ANSWERS = 2;

// Răspunsul corect pe server: biletele de examen (EXAM) îl țin de la 1, cele de
// teorie (PRACTICE, P1–P24) de la 0 — exams.service.ts îl compară direct cu ce
// trimite elevul. Aici, în ciorne (`correctAnswer`), e mereu de la 1; `ticketType`
// spune cum se citește de pe server și cum se scrie înapoi.
function correctAnswerBase(ticketType) {
   return String(ticketType ?? "").trim().toUpperCase() === "PRACTICE" ? 0 : 1;
}

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function toPositiveInt(value) {
   const n = Number(value);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function firstNonEmpty(...values) {
   for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
   }
   return "";
}

export function parseJsonArraySafe(value, fallback = []) {
   if (Array.isArray(value)) return value;
   if (typeof value !== "string") return fallback;

   const text = String(value ?? "").trim();
   if (!text) return fallback;

   try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : fallback;
   } catch {
      return fallback;
   }
}

export function parseLocalizedTextRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO, value?.textRo, value?.text_ro),
         ru: firstNonEmpty(value?.ru, value?.RU, value?.textRu, value?.text_ru),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedTextRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

export function parseLocalizedAnswerRaw(value) {
   if (isObject(value)) {
      return {
         ro: firstNonEmpty(value?.ro, value?.RO),
         ru: firstNonEmpty(value?.ru, value?.RU),
      };
   }

   const text = String(value ?? "").trim();
   if (!text) return { ro: "", ru: "" };

   try {
      const parsed = JSON.parse(text);
      if (isObject(parsed)) return parseLocalizedAnswerRaw(parsed);
   } catch {}

   return { ro: text, ru: "" };
}

export function stringifyLocalizedAnswer(answer) {
   const ro = String(answer?.ro ?? "").trim();
   const ru = String(answer?.ru ?? "").trim();

   return JSON.stringify({
      ro: ro || ru,
      ru: ru || ro,
   });
}

function readFallbackAnswerSeries(raw, builders = []) {
   const values = [];
   let hasAtLeastOne = false;

   for (let index = 1; index <= 6; index += 1) {
      let picked;

      for (const buildKey of builders) {
         const key = buildKey(index);
         if (raw?.[key] != null) {
            picked = raw[key];
            break;
         }
      }

      if (picked != null) hasAtLeastOne = true;
      values.push(picked);
   }

   return hasAtLeastOne ? values : [];
}

function getValueByLooseKeys(obj, keys = []) {
   if (!isObject(obj)) return undefined;
   const normalizedKeys = keys.map((key) =>
      String(key ?? "")
         .toLowerCase()
         .replace(/[^a-z0-9]/g, ""),
   );

   for (const [key, value] of Object.entries(obj)) {
      const normalizedKey = String(key ?? "")
         .toLowerCase()
         .replace(/[^a-z0-9]/g, "");
      if (normalizedKeys.includes(normalizedKey)) {
         return value;
      }
   }

   return undefined;
}

function inferCorrectAnswerFromAnswerFlags(answersBase = []) {
   for (let i = 0; i < answersBase.length; i += 1) {
      const answer = answersBase[i];
      if (!isObject(answer)) continue;

      const flag =
         answer.correct ??
         answer.isCorrect ??
         answer.right ??
         answer.ok ??
         answer.valid ??
         false;

      if (flag === true) return i + 1;
   }

   return null;
}

function normalizeCorrectAnswer(rawValue, answersCount) {
   const text = String(rawValue ?? "").trim();
   if (!text) return null;

   let n = null;

   if (/^[+-]?\d+$/.test(text)) {
      n = Number(text);
   } else {
      const upper = text.toUpperCase();

      if (/^[A-F]$/.test(upper)) {
         n = upper.charCodeAt(0) - 64;
      }
   }

   if (!Number.isInteger(n) || answersCount <= 0) return null;
   if (n >= 1 && n <= answersCount) return n;
   if (n >= 0 && n < answersCount) return n + 1;
   return null;
}

export function cloneLocalizedAnswers(answers) {
   return (Array.isArray(answers) ? answers : []).map((answer) => ({
      ro: String(answer?.ro ?? ""),
      ru: String(answer?.ru ?? ""),
   }));
}

export function normalizeExamQuestionRecord(raw, index = 0, { ticketType } = {}) {
   if (!isObject(raw)) return null;

   const id = toPositiveInt(raw?.id ?? raw?.questionId);
   if (!id) return null;

   const textLocalized = parseLocalizedTextRaw(
      raw?.text ?? raw?.question ?? raw?.title ?? raw?.content ?? raw?.questionText,
   );

   const textRo = firstNonEmpty(
      raw?.textRo,
      raw?.text_ro,
      raw?.textRO,
      textLocalized.ro,
   );
   const textRu = firstNonEmpty(
      raw?.textRu,
      raw?.text_ru,
      raw?.textRU,
      textLocalized.ru,
   );

   const answersBase = parseJsonArraySafe(
      raw?.answers,
      readFallbackAnswerSeries(raw, [
         (i) => `a${i}`,
         (i) => `answer${i}`,
      ]),
   );
   const answersRuBase = parseJsonArraySafe(
      raw?.answersRu ?? raw?.answersRU ?? raw?.answers_ru,
      readFallbackAnswerSeries(raw, [
         (i) => `a${i}Ru`,
         (i) => `a${i}RU`,
         (i) => `a${i}_ru`,
         (i) => `answer${i}Ru`,
         (i) => `answer${i}RU`,
         (i) => `answer${i}_ru`,
      ]),
   );

   const answers = [];
   const sourceLen = Math.max(answersBase.length, answersRuBase.length);

   for (let idx = 0; idx < sourceLen; idx += 1) {
      const roValue = parseLocalizedAnswerRaw(answersBase[idx]);
      const ruValue = parseLocalizedAnswerRaw(answersRuBase[idx]);

      const ro = firstNonEmpty(roValue?.ro, roValue?.ru);
      const ru = firstNonEmpty(ruValue?.ru, ruValue?.ro, roValue?.ru);

      if (!ro && !ru) continue;

      answers.push({
         ro: ro || ru,
         ru: ru || ro,
      });
   }

   while (answers.length < MIN_EXAM_ANSWERS) {
      answers.push({ ro: "", ru: "" });
   }

   const keyedCorrectAnswer = getValueByLooseKeys(raw, [
      "correctAnswer",
      "correct_answer",
      "correctIndex",
      "correct_index",
      "rightIndex",
      "right_index",
      "answerIndex",
      "answer_index",
      "rightAnswer",
      "right_answer",
      "goodAnswer",
      "good_answer",
      "solution",
      "answer",
   ]);
   const correctAnswerRaw =
      keyedCorrectAnswer ?? inferCorrectAnswerFromAnswerFlags(answersBase);

   // teoria: numărul de pe server e de la 0 (fără ghicit: 1 e al doilea răspuns)
   const keyedText = String(keyedCorrectAnswer ?? "").trim();
   const zeroBased =
      correctAnswerBase(ticketType) === 0 && /^\d+$/.test(keyedText);
   const parsedCorrectAnswer = zeroBased
      ? Number(keyedText) < answers.length
         ? Number(keyedText) + 1
         : null
      : normalizeCorrectAnswer(correctAnswerRaw, answers.length);

   const hintLocalized = parseLocalizedTextRaw(
      raw?.hint ?? raw?.explanation ?? raw?.hintText,
   );
   const hintRo = firstNonEmpty(raw?.hintRo, raw?.hint_ro, hintLocalized.ro);
   const hintRu = firstNonEmpty(raw?.hintRu, raw?.hint_ru, hintLocalized.ru);

   return {
      id,
      order: toPositiveInt(raw?.order) ?? index + 1,
      textRo,
      textRu,
      image: firstNonEmpty(raw?.image, raw?.img),
      hintRo,
      hintRu,
      answers,
      correctAnswer: parsedCorrectAnswer || 1,
      categoryId:
         raw?.categoryId == null ? null : (toPositiveInt(raw?.categoryId) ?? null),
   };
}

export function buildExamQuestionPutPayload(draft, { ticketType } = {}) {
   const textRo = String(draft?.textRo ?? "").trim();
   const textRu = String(draft?.textRu ?? "").trim();

   if (!textRo && !textRu) {
      throw new Error("Întrebarea trebuie să aibă text în cel puțin o limbă.");
   }

   const answers = cloneLocalizedAnswers(draft?.answers).map((answer) => ({
      ro: String(answer?.ro ?? "").trim(),
      ru: String(answer?.ru ?? "").trim(),
   }));

   if (answers.length < MIN_EXAM_ANSWERS) {
      throw new Error(`Sunt necesare minimum ${MIN_EXAM_ANSWERS} răspunsuri.`);
   }

   if (answers.some((answer) => !answer.ro && !answer.ru)) {
      throw new Error("Toate răspunsurile trebuie completate înainte de salvare.");
   }

   const correctAnswer = Number(draft?.correctAnswer);
   if (
      !Number.isInteger(correctAnswer) ||
      correctAnswer < 1 ||
      correctAnswer > answers.length
   ) {
      throw new Error("Răspunsul corect este invalid.");
   }

   const categoryText = String(draft?.categoryId ?? "").trim();
   const categoryId = !categoryText ? null : toPositiveInt(categoryText);
   if (categoryText && categoryId == null) {
      throw new Error("Categoria selectată este invalidă.");
   }

   const payload = {
      text: JSON.stringify({
         ro: textRo,
         ru: textRu,
      }),
      answers: answers.map((answer) => stringifyLocalizedAnswer(answer)),
      // ciorna e de la 1; teoria se scrie de la 0
      correctAnswer: correctAnswer - 1 + correctAnswerBase(ticketType),
      categoryId,
      image: String(draft?.image ?? "").trim(),
   };

   const hintRo = String(draft?.hintRo ?? "").trim();
   const hintRu = String(draft?.hintRu ?? "").trim();
   if (hintRo || hintRu) {
      payload.hint = JSON.stringify({
         ro: hintRo || hintRu,
         ru: hintRu || hintRo,
      });
   }

   return payload;
}
